import { forwardRef, useId } from 'react';
import ReactSelect, {
  components,
  ClearIndicatorProps,
  MultiValueRemoveProps,
  Props as SelectProps,
  PlaceholderProps,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { twMerge } from 'tailwind-merge';
import { inputStyles, Label } from 'src/components/ui/Field';
import { dropdownItemStyles } from 'src/components/ui/Select';
import { popoverStyles } from 'src/components/ui/Popover';
import CloseIcon from 'src/assets/icons/close.svg';

import type { ReactNode } from 'react';
import type { ColorVariant, Size } from 'src/types/common';

export type AutoCompleteOption = {
  label: string;
  value: string | number;
  [key: string]: unknown;
};

export interface AutoCompleteProps extends SelectProps<
  AutoCompleteOption,
  boolean
> {
  label?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  variant?: ColorVariant;
  size?: Size;
  'data-rhf-name'?: string;
  /**
   * Let the user commit a value that is not in the options, by typing it.
   * The `onChange` option then carries `__isNew__: true`, with `value` and
   * `label` both set to what they typed.
   *
   * Off by default — every existing caller picks from a fixed list, and
   * silently allowing invented values there would be a behaviour change.
   */
  creatable?: boolean;
  /** Label for the "create this" row. Only read when `creatable`. */
  formatCreateLabel?: (inputValue: string) => ReactNode;
}

const AutoComplete = forwardRef<any, AutoCompleteProps>(
  (
    {
      isInvalid,
      isRequired,
      errorMessage,
      variant = 'surface',
      size = 'medium',
      className,
      components: componentItems,
      'data-rhf-name': dataRhfName,
      creatable = false,
      ...props
    },
    ref
  ) => {
    const inputId = useId();
    const { label } = props;
    // Same component, same props, same styling — CreatableSelect is
    // react-select's own subclass, so this only adds the "type a new one" row.
    const SelectComponent = creatable ? CreatableSelect : ReactSelect;

    return (
      <div
        className={twMerge('group flex flex-col gap-1', className)}
        data-rhf-name={dataRhfName}
      >
        {label && (
          <Label htmlFor={inputId} isRequired={isRequired}>
            {label}
          </Label>
        )}
        <SelectComponent
          ref={ref}
          inputId={inputId}
          closeMenuOnSelect={false}
          unstyled
          styles={{
            input: (base) => ({
              ...base,
              'input:focus': {
                boxShadow: 'none',
              },
            }),
            // On mobile, the label will truncate automatically, so we want to
            // override that behaviour.
            multiValueLabel: (base) => ({
              ...base,
              whiteSpace: 'normal',
              overflow: 'visible',
            }),
            control: (base) => ({
              ...base,
              transition: 'none',
            }),
          }}
          components={{
            DropdownIndicator,
            ClearIndicator,
            MultiValueRemove,
            IndicatorSeparator,
            ...componentItems,
          }}
          classNames={{
            control: () =>
              inputStyles({
                className: `bg-surface-alt px-4 py-1 font-semibold text-surface-alt-90 !h-auto ${size === 'small' ? '!min-h-8' : '!min-h-11'} ${isInvalid ? '!border-red' : 'focus-within:border-surface-ghost-45'} focus-within:outline-2 focus-within:shadow-[0_0_0_4px_var(--focus-halo)]`,
                variant,
                size,
                isInvalid,
              }),
            placeholder: (placeholderProps) => {
              return placeholderStyles(placeholderProps);
            },
            input: () => selectInputStyles,
            valueContainer: () => valueContainerStyles,
            singleValue: () => singleValueStyles,
            multiValue: () => multiValueStyles,
            multiValueLabel: () => multiValueLabelStyles,
            multiValueRemove: () => multiValueRemoveStyles,
            indicatorsContainer: () => indicatorsContainerStyles,
            clearIndicator: () => clearIndicatorStyles,
            indicatorSeparator: () => indicatorSeparatorStyles,
            dropdownIndicator: () => dropdownIndicatorStyles,
            menu: () => menuStyles,
            groupHeading: () => groupHeadingStyles,
            option: ({ isFocused, isSelected, isDisabled }) =>
              dropdownItemStyles({
                isHovered: isSelected || isFocused,
                isDisabled,
                className:
                  'flex-1 flex items-center justify-between gap-2 truncate text-sm font-semibold text-surface-alt-ghost-75',
              }),
            noOptionsMessage: () => noOptionsMessageStyles,
          }}
          {...props}
        />
        {isInvalid && (
          <span role="alert" className="text-xs text-red font-medium">
            {errorMessage}
          </span>
        )}
      </div>
    );
  }
);

AutoComplete.displayName = 'AutoComplete';

const IndicatorSeparator = () => null;
const DropdownIndicator = () => null;

const ClearIndicator = (
  props: ClearIndicatorProps<AutoCompleteOption, boolean>
) => {
  const { innerProps, selectProps } = props;
  const a11ySelectProps = selectProps as AutoCompleteProps;
  const fieldName =
    a11ySelectProps['aria-label'] ?? a11ySelectProps.label ?? 'selection';

  return (
    <components.ClearIndicator
      {...props}
      innerProps={{
        ...innerProps,
        'aria-label': `Clear ${fieldName}`,
      }}
    >
      <CloseIcon className="w-4 h-4" />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (
  props: MultiValueRemoveProps<AutoCompleteOption, boolean>
) => {
  const { data, innerProps } = props;
  const itemName = data.label || 'selected item';

  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        ...innerProps,
        'aria-label': `Remove ${itemName}`,
      }}
    >
      <CloseIcon className="w-2.5 h-2.5" />
    </components.MultiValueRemove>
  );
};

const placeholderStyles = ({
  isMulti,
}: PlaceholderProps<AutoCompleteOption, boolean>) =>
  `text-surface-alt-75 font-medium pl-1 py-0.5 ${isMulti ? '' : 'whitespace-nowrap'}`;
const selectInputStyles = 'pl-1 py-0.5';
const valueContainerStyles = 'gap-1';
const singleValueStyles = 'leading-7 ml-1';
const multiValueStyles =
  'bg-secondary-ghost-alt rounded-full items-center pl-2.5 pr-1.5 gap-1';
const multiValueLabelStyles =
  'py-0.5 text-secondary-ghost-alt-75 text-md font-medium';
const multiValueRemoveStyles =
  'inline-flex items-center justify-center flex-shrink-0 w-4 h-4 bg-secondary-ghost-alt-hover rounded-full';
const indicatorsContainerStyles = 'gap-1';
const clearIndicatorStyles =
  'inline-flex items-center justify-center flex-shrink-0 w-6 h-6 text-surface-alt-ghost-75 bg-surface-alt-ghost-hover rounded-full';
const indicatorSeparatorStyles = '';
const dropdownIndicatorStyles = '';
const menuStyles = popoverStyles({ className: 'p-1.5' });
const groupHeadingStyles = 'ml-3 mt-2 mb-1 text-gray-500 text-sm';
const noOptionsMessageStyles =
  'text-sm font-semibold text-surface-alt-ghost-75 p-2';

export default AutoComplete;
